import PropTypes from "prop-types";
import React, { useReducer } from "react";
import { DateTime } from "luxon";
import { sortBy, orderBy } from "lodash";

import API from "../../API";
import { StringUtils, Parsers } from "../../util";
import {
  POPULATE_RECEIPT,
  POPULATE_GROUP_RECEIPT,
  POPULATE_RESERVATIONS,
  POPULATE_RIDERS,
  POPULATE_RIDER_COUNT,
  POPULATE_WALLET,
  POPULATE_WALLET_TRANSACTIONS,
  SET_ACTIVE_RESERVATION,
  SET_ACTIVE_GROUP_RESERVATION,
  SET_ACTIVE_RIDER,
  SET_ACCOUNT_STATUS,
  POPULATE_RESERVATION_CREDITS,
  POPULATE_CUSTOMER_COUPONS,
  POPULATE_RESERVATION_FLEET,
  POPULATE_ACTIVE_PASS,
  POPULATE_PASS_TRANSACTIONS,
  POPULATE_FLEETS,
  POPULATE_PASS_OFFERS,
  POPULATE_ACTIVE_TRIP,
  POPULATE_USER_DEVICES,
  SET_BAN_AND_SUSPENSION_REASON,
  POPULATE_GROUP_RESERVATIONS,
} from "../types";
import { ACCOUNT_STATUS, BANS_AND_SUSPENSION_REASON, CashOutTransactions } from "../../constants";
import RiderContext from "./riderContext";
import riderReducer from "./riderReducer";
import uuidFromUrl from "../../util/strings/uuidFromUrl";

const RiderState = ({ children }) => {
  const initialState = {
    riders: [],
    riderCount: 0,
    activeRider: null,
    accountStatus: null,
    reservations: {},
    groupReservations: {},
    activeReservation: null,
    activeGroupReservation: null,
    wallet: null,
    walletTransactions: null,
    passTransactions: [],
    receipt: null,
    groupReceipt: null,
    reservationCredits: null,
    activeTrip: null,
    customerCoupons: [],
    reservationFleet: null,
    activePass: null,
    fleets: [],
    passOffers: [],
    userDevices: null,
    reasonForBanOrSuspension: {},
  };

  const [state, dispatch] = useReducer(riderReducer, initialState);

  const parseReservation = (r) => ({
    ...r,
    tripId: r.trip?.url ? StringUtils.uuidFromUrl(r.trip?.url) : null,
    vehicleId: r.vehicle ? StringUtils.uuidFromUrl(r.vehicle) : null,
    start: Parsers.date(r.start),
    stop: Parsers.date(r.stop),
    vehicleCode: r.shortcode ? r.shortcode.split("/").pop() : null,
    fleets: r.fleets ? r.fleets : [],
  });

  const parseGroupReservation = (groupReservation) => {
    if (!groupReservation) return null;
    const { reservations, guests } = groupReservation;

    if (!reservations.length)
      return {
        ...groupReservation,
        hostReservation: null,
        start: null,
        stop: null,
      };

    const sortedReservations = orderBy(
      // the intention of this orderBy method is to bubble the host reservation(s) to the top of the list
      // the reservation(s) that belong to the host, are any reservations whose ID is not included in the guests array
      // it's possible that there are multiple reservations that belong to the host, so we sort by start time as well
      reservations.map((r) => {
        const reservation = { ...r };
        reservation.shortcode = r.shortcode ? r.shortcode.split("/").at(-1) : "";
        reservation.guest = guests.find((g) => g.reservation === r.id) || null;
        return reservation;
      }),
      ["guest", "created"],
      ["desc", "asc"],
    );

    const start = Parsers.date(sortedReservations[0].start);
    const stop = Parsers.date(sortedReservations.at(-1).stop);

    return {
      ...groupReservation,
      reservations: sortedReservations,
      hostReservation: sortedReservations[0],
      start,
      stop,
    };
  };

  const searchRiders = async (phoneNum, pageSize = 50, page = 1) => {
    if (!phoneNum) {
      dispatch({
        type: POPULATE_RIDERS,
        payload: [],
      });
      dispatch({
        type: POPULATE_RIDER_COUNT,
        payload: 0,
      });
      return;
    }
    try {
      const data = await API.riders.list({ phone_contains: phoneNum, page_size: pageSize, page });
      const riders = data.results.map((r) =>
        r.url ? { ...r, id: StringUtils.uuidFromUrl(r.url) } : r,
      );
      dispatch({
        type: POPULATE_RIDERS,
        payload: riders,
      });
      dispatch({
        type: POPULATE_RIDER_COUNT,
        payload: data.count,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchPassTransactions = async (customerId, passId) => {
    try {
      const data = await API.billing.getPassTransactions(customerId, passId);
      dispatch({
        type: POPULATE_PASS_TRANSACTIONS,
        payload: data,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReservations = async (riderId, params) => {
    if (!riderId) {
      dispatch({
        type: POPULATE_RESERVATIONS,
        payload: [],
      });
      return;
    }
    try {
      const data = await API.reservations.getByRider(riderId, params);
      const { results, count, next, previous } = data;
      let currentPage = 1;
      if (next) {
        const nextUrl = new URL(next).searchParams;
        currentPage = parseInt(nextUrl.get("page"), 10) - 1;
      } else if (previous) {
        const prevUrl = new URL(previous).searchParams;
        currentPage = parseInt(prevUrl.get("page") ?? 1, 10) + 1;
      }
      const parsedData = results
        .map(parseReservation)
        .sort((a, b) => (a.start && b.start ? b.start - a.start : a.id.localeCompare(b.id)));
      dispatch({
        type: POPULATE_RESERVATIONS,
        payload: { data: parsedData, currentPage, count },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGroupReservations = async (id, params) => {
    if (!id) {
      dispatch({
        type: POPULATE_GROUP_RESERVATIONS,
        payload: [],
      });
      return;
    }
    try {
      const data = await API.reservations.getRiderGroupReservations(id, params);
      const { results, count, next, previous } = data;
      let currentPage = 1;
      if (next) {
        const nextUrl = new URL(next).searchParams;
        currentPage = parseInt(nextUrl.get("page"), 10) - 1;
      } else if (previous) {
        const prevUrl = new URL(previous).searchParams;
        currentPage = parseInt(prevUrl.get("page") ?? 1, 10) + 1;
      }
      const parsedData = results
        .map(parseGroupReservation)
        .sort((a, b) => (a.start && b.start ? b.start.getTime() - a.start.getTime() : 1));

      dispatch({
        type: POPULATE_GROUP_RESERVATIONS,
        payload: { data: parsedData, currentPage, count },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const getRemainder = (times) => {
    let output;
    const allKeys = Object.keys(times);
    for (let i = 0; i < allKeys.length; i++) {
      const key = allKeys[i];
      if (parseInt(times[key], 10) > 0) {
        output = `${times[key]}${key[0]}`;
        break;
      }
    }
    if (!output) return "<1m";
    return output;
  };

  const setAccountStatus = (suspensions) => {
    let status = ACCOUNT_STATUS.Active;

    if (state.activeRider.is_deleted) {
      status = ACCOUNT_STATUS.Deleted;
    } else if (suspensions.length) {
      const { start, end } = suspensions[suspensions.length - 1];
      const dateOfChange = DateTime.fromISO(start).toFormat("LLL dd");
      if (end) {
        const now = DateTime.now();
        const suspensionEnd = DateTime.fromISO(end);
        const timeRemaining = suspensionEnd.diff(now, ["days", "hours", "minutes"]).values;

        if (Object.values(timeRemaining).some((val) => val > 0)) {
          status = `${ACCOUNT_STATUS.Suspended} (${getRemainder(timeRemaining)}, ${dateOfChange})`;
        }
      } else {
        status = `${ACCOUNT_STATUS.Banned} (${dateOfChange})`;
      }
    }

    dispatch({
      type: SET_ACCOUNT_STATUS,
      payload: status,
    });
  };

  const setReasonForBanAndSuspension = (reasons, notes) => {
    let reasonForBan = "";
    let remarks = "";

    if (state.activeRider.is_deleted) {
      reasonForBan = BANS_AND_SUSPENSION_REASON.other;
      remarks = "";
    } else if (reasons) {
      reasonForBan = reasons;
      remarks = notes;
    }

    dispatch({
      type: SET_BAN_AND_SUSPENSION_REASON,
      payload: { reasonForBan, remarks },
    });
  };

  const activateAccount = async (rider) => {
    const { suspensions } = rider;
    const suspensionId = suspensions.pop().id;

    let updatedSuspension;
    try {
      updatedSuspension = await API.riders.activate(suspensionId);
    } catch (err) {
      console.error(err);
    }

    if (updatedSuspension) {
      setAccountStatus([...suspensions, updatedSuspension]);
    }
  };

  const banOrSuspendAccount = async (riderId, length, reason, notes) => {
    let suspensions = state.activeRider.suspensions;

    if (riderId) {
      try {
        const start = DateTime.now();
        const end = length ? start.plus({ days: parseFloat(length, 10) }).toISO() : null;
        const newSuspension = await API.riders.banOrSuspend(
          riderId,
          start.toISO(),
          end,
          reason,
          notes,
        );
        suspensions = [...suspensions, newSuspension];
      } catch (err) {
        console.error(err);
      }
    }

    dispatch({
      type: SET_ACTIVE_RIDER,
      payload: {
        ...state.activeRider,
        suspensions,
      },
    });
    setAccountStatus(suspensions);
  };

  const softDeleteAccount = async (rider) => {
    const { id } = rider;
    if (id) {
      try {
        await API.riders.softDelete(id);
        setActiveRider(id);
      } catch (err) {
        console.error(err);
      }
    }
  };

  const updateRiderInformation = async (riderId, data) => {
    let rider = {};
    if (riderId) {
      try {
        rider = await API.riders.updateRiderInformation(riderId, data);
      } catch (err) {
        console.error(err);
      }
    }
    dispatch({
      type: SET_ACTIVE_RIDER,
      payload: rider,
    });
  };

  const setActiveRider = async (id) => {
    let rider = null;
    if (id) {
      if (state.riders.length) {
        rider = state.riders.find((r) => r.id.toLowerCase() === id.toLowerCase());
      }
      if (!rider) {
        try {
          const data = await API.riders.getRider(id);
          rider = data.results[0];
          rider.id = StringUtils.uuidFromUrl(rider.url);
        } catch (err) {
          console.error(err);
        }
      }
      dispatch({
        type: SET_ACTIVE_RIDER,
        payload: rider,
      });
    }
  };

  const fetchActiveUserDevices = async (userId) => {
    if (!userId) {
      dispatch({
        type: POPULATE_USER_DEVICES,
        payload: [],
      });
      return;
    }
    try {
      const userDevices = await API.riders.getRiderDevices(userId);
      dispatch({
        type: POPULATE_USER_DEVICES,
        payload: userDevices,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const setActiveGroupReservation = async (id, useCache = true) => {
    let groupReservation = null;
    if (id) {
      if (useCache && state.groupReservations.data?.length) {
        groupReservation = state.groupReservations.data?.filter(
          (r) => uuidFromUrl(r.url) === id,
        )[0];
      }
      if (!groupReservation) {
        try {
          groupReservation = await API.groupReservations.get(id);
        } catch (err) {
          console.error(err);
        }
      }
    }

    dispatch({
      type: SET_ACTIVE_GROUP_RESERVATION,
      payload: parseGroupReservation(groupReservation),
    });
  };

  const setActiveReservation = async (id, useCache = true) => {
    let reservation = null;
    let parking = null;
    if (id) {
      if (useCache && state.reservations.data?.length) {
        reservation = state.reservations.data?.filter((r) => uuidFromUrl(r.url) === id)[0];
      }
      if (!reservation) {
        try {
          reservation = await API.reservations.get(id);
        } catch (err) {
          console.error(err);
        }
      }
      try {
        parking = await API.reservations.getParkingPhoto(id);
        reservation = { ...reservation, parking };
      } catch (err) {
        console.error(err);
      }
      reservation = parseReservation(reservation);
    }
    dispatch({
      type: SET_ACTIVE_RESERVATION,
      payload: reservation,
    });
  };

  const fetchReservationByTrip = async (tripId) => {
    const reservation = await API.reservations.getByTripId(tripId);
    if (reservation.id) {
      setActiveReservation(reservation.id);
    }
    return reservation;
  };

  const endReservation = async (id) => {
    try {
      await API.reservations.endReservation(id);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFleet = async (id) => {
    if (!id) {
      dispatch({
        type: POPULATE_RESERVATION_FLEET,
        payload: null,
      });
      return;
    }
    try {
      const fleet = await API.fleets.get(id);
      dispatch({
        type: POPULATE_RESERVATION_FLEET,
        payload: fleet,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWallet = async (riderId) => {
    if (!riderId) {
      dispatch({
        type: POPULATE_WALLET,
        payload: null,
      });
      return;
    }
    try {
      const wallet = await API.billing.getWallet(riderId);

      dispatch({
        type: POPULATE_WALLET,
        payload: wallet,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const changeWalletAmount = (amount) => {
    dispatch({
      type: POPULATE_WALLET,
      payload: {
        ...state.wallet,
        balance: {
          ...state.wallet.balance,
          real: state.wallet.balance.real + amount,
          total: state.wallet.balance.total + amount,
        },
      },
    });
  };

  const fetchWalletTransactions = async (id) => {
    if (!id) {
      dispatch({
        type: POPULATE_WALLET_TRANSACTIONS,
        payload: null,
      });
      return;
    }
    try {
      const walletTransactions = await API.billing.getWalletTransactions(id);
      const parsedWalletTransactions = (walletTransactions || [])
        .filter(
          (wt) =>
            (wt.amount !== 0 ||
              wt.bonus_amount !== 0 ||
              // We will accept a transaction that has both 0 in amount and bonus_amount if it is a canceled card hold
              (wt.status === "canceled" && wt.reference_type === "CARD_HOLD")) &&
            !(wt.type === "debit" && CashOutTransactions.includes(wt.reference_type)),
        )
        .map((wt) => ({ ...wt, reference_type: wt.reference_type.toUpperCase() }));

      dispatch({
        type: POPULATE_WALLET_TRANSACTIONS,
        payload: parsedWalletTransactions,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchReceipt = async (reservationId) => {
    if (!reservationId) {
      dispatch({
        type: POPULATE_RECEIPT,
        payload: null,
      });
      return;
    }
    try {
      const receipt = await API.billing.getReceipt(reservationId);
      dispatch({
        type: POPULATE_RECEIPT,
        payload: Parsers.receipt(receipt),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchGroupReceipt = async (groupReservationId) => {
    if (!groupReservationId) {
      dispatch({
        type: POPULATE_GROUP_RECEIPT,
        payload: null,
      });
      return;
    }
    try {
      let receipt = await API.billing.getGroupReceipt(groupReservationId);
      receipt = {
        ...receipt,
        line_items: sortBy(receipt.line_items, "subtrip_created"),
      };

      dispatch({
        type: POPULATE_GROUP_RECEIPT,
        payload: Parsers.receipt(receipt),
      });
    } catch (err) {
      console.error(err);
    }
  };

  const alterBonus = async (amount, riderId = state.activeRider?.id) => {
    try {
      if (amount > 0) {
        await API.billing.addBonus(riderId, amount);
      }
      if (amount < 0) {
        await API.billing.removeBonus(riderId, Math.abs(amount));
      }
      await fetchWallet(riderId);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const fetchReservationCredits = async (reservationId = state.activeReservation?.id) => {
    try {
      const credits = await API.billing.getCredits(reservationId);
      dispatch({
        type: POPULATE_RESERVATION_CREDITS,
        payload: credits,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const creditWallet = async (
    amount,
    reservationId = state.activeReservation?.id,
    riderId = state.activeRider?.id,
  ) => {
    try {
      if (amount <= 0) throw new Error("Can not credit wallet amount < 0");
      await API.billing.creditWallet(reservationId, amount);
      await Promise.all([fetchReservationCredits(reservationId), fetchWallet(riderId)]);
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const issueFine = async (tripId, data) => {
    try {
      const res = await API.billing.issueFine(tripId, data);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    }
  };

  const fetchActiveTrip = async (reservationId) => {
    if (!reservationId)
      dispatch({
        type: POPULATE_ACTIVE_TRIP,
        payload: null,
      });
    try {
      const trip = await API.billing.getBillingTrip(reservationId);
      dispatch({
        type: POPULATE_ACTIVE_TRIP,
        payload: trip,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const creditPassTrip = async (reservationId) => {
    try {
      if (!state.activeTrip?.pass_use_refunded) {
        await API.billing.refundSinglePassUse(reservationId);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const cashOutTransaction = async (amount, transactionId, riderId = state.activeRider?.id) => {
    if (amount <= 0) throw new Error("Can not cash out wallet amount < 0");
    if (riderId && transactionId) {
      await API.billing.cashOutTransaction(riderId, transactionId, amount);
      changeWalletAmount(-amount);
    }
  };

  const cashOutTripTransaction = async (amount, tripTransactionId, tripId) => {
    if (amount <= 0) throw new Error("Can not cash out wallet amount < 0");
    if (tripId && tripTransactionId) {
      await API.billing.cashOutTripTransaction(tripId, tripTransactionId, amount);
    }
  };
  const cashOutFineTransaction = async (amount, fineTransactionId, tripId) => {
    if (amount <= 0) throw new Error("Can not cash out wallet amount < 0");
    if (tripId && fineTransactionId) {
      await API.billing.cashOutFineTransaction(tripId, fineTransactionId, amount);
    }
  };

  const cashOutPassTransaction = async (
    amount,
    passTransactionId,
    riderId = state.activeRider?.id,
  ) => {
    try {
      if (amount <= 0) throw new Error("Can not cash out pass amount < 0");
      if (riderId && passTransactionId) {
        await API.billing.cashOutPassTransaction(riderId, passTransactionId, amount);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCustomerCoupons = async (id) => {
    try {
      if (!id) {
        dispatch({
          type: POPULATE_CUSTOMER_COUPONS,
          payload: [],
        });
        return;
      }
      const coupons = await API.billing.getCustomerCoupons(id);
      dispatch({
        type: POPULATE_CUSTOMER_COUPONS,
        payload: coupons,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchActivePass = async (riderId) => {
    try {
      if (!riderId) {
        dispatch({
          type: POPULATE_ACTIVE_PASS,
          payload: null,
        });
        return;
      }
      const [pass] = await API.billing.getActivePass(riderId);
      dispatch({
        type: POPULATE_ACTIVE_PASS,
        payload: pass,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const deactivatePass = async (passId = state.activePass?.id) => {
    try {
      if (!passId) {
        return;
      }
      await API.billing.deactivatePass(passId);
      dispatch({
        type: POPULATE_ACTIVE_PASS,
        payload: null,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFleets = async () => {
    if (!state.fleets.length) {
      try {
        const data = await API.fleets.list({ inactive: false });
        const fleets = data.map((fleet) => ({
          ...fleet,
          uuid4: StringUtils.uuidFromUrl(fleet.url),
        }));
        fleets.sort((fleet1, fleet2) =>
          fleet1?.name.toLowerCase() >= fleet2?.name.toLowerCase() ? 1 : -1,
        );
        dispatch({
          type: POPULATE_FLEETS,
          payload: fleets,
        });
      } catch (err) {
        console.error(err);
      }
    }
  };

  const fetchPassOffers = async (fleetId) => {
    if (!fleetId) {
      dispatch({
        type: POPULATE_PASS_OFFERS,
        payload: [],
      });
      return;
    }
    try {
      const data = await API.billing.getPassOffers({ disabled: false, fleet: fleetId });
      dispatch({
        type: POPULATE_PASS_OFFERS,
        payload: data.results,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const addPass = async (data) => {
    try {
      if (!data) return;
      const pass = await API.billing.addPass(data);
      dispatch({
        type: POPULATE_ACTIVE_PASS,
        payload: pass,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const clearNegativeBalance = async (riderId = state.activeRider?.id) => {
    try {
      if (!riderId) return;
      await API.billing.zeroOutNegativeBalance(riderId);
      await fetchWalletTransactions(riderId);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <RiderContext.Provider
      value={{
        riders: state.riders,
        riderCount: state.riderCount,
        activeRider: state.activeRider,
        accountStatus: state.accountStatus,
        reservations: state.reservations,
        groupReservations: state.groupReservations,
        activeReservation: state.activeReservation,
        activeGroupReservation: state.activeGroupReservation,
        wallet: state.wallet,
        walletTransactions: state.walletTransactions,
        receipt: state.receipt,
        groupReceipt: state.groupReceipt,
        reservationCredits: state.reservationCredits,
        customerCoupons: state.customerCoupons,
        reservationFleet: state.reservationFleet,
        activePass: state.activePass,
        activeTrip: state.activeTrip,
        fleets: state.fleets,
        passOffers: state.passOffers,
        passTransactions: state.passTransactions,
        userDevices: state.userDevices,
        reasonForBanOrSuspension: state.reasonForBanOrSuspension,

        searchRiders,
        fetchReservations,
        fetchGroupReservations,
        fetchWallet,
        fetchWalletTransactions,
        fetchReceipt,
        fetchGroupReceipt,
        fetchFleet,
        setActiveRider,
        setAccountStatus,
        setReasonForBanAndSuspension,
        banOrSuspendAccount,
        softDeleteAccount,
        activateAccount,
        setActiveReservation,
        setActiveGroupReservation,
        endReservation,
        alterBonus,
        fetchReservationCredits,
        fetchReservationByTrip,
        creditWallet,
        cashOutTransaction,
        cashOutTripTransaction,
        cashOutPassTransaction,
        cashOutFineTransaction,
        fetchCustomerCoupons,
        fetchActivePass,
        clearNegativeBalance,
        deactivatePass,
        fetchFleets,
        fetchPassOffers,
        addPass,
        creditPassTrip,
        fetchActiveTrip,
        fetchPassTransactions,
        fetchActiveUserDevices,
        updateRiderInformation,
        issueFine,
      }}
    >
      {children}
    </RiderContext.Provider>
  );
};

RiderState.propTypes = {
  children: PropTypes.node.isRequired,
};

export default RiderState;
